<template>
  <div
    v-if="plan && plan.merchant_id"
    class="plan-bundle-list-item"
    @click="updateSelectedPlan(plan)"
    :id="cardIdValue"
  >
    <div class="plan-bundle-list-item__badge-wrapper">
      <img
        :src="plan.badge"
        :alt="plan.name"
        :class="[
          'plan-bundle-list-item__badge-img',
          `plan-bundle-list-item__badge-img--${plan.merchant_id}`,
        ]"
      />
    </div>
    <div class="plan-bundle-list-item__price">
      {{ `$${price}` }}
    </div>
    <div class="plan-bundle-list-item__radio-button">
      <RadioButton :isSelected="selected" />
    </div>
    <div class="plan-bundle-list-item__name-wrapper">
      <h4 class="plan-bundle-list-item__name">
        {{ plan.nameShort }}
        <span class="plan-bundle-list-item__name--promo-info">{{ plan.promoInfo.title }}</span>
      </h4>
      <div class="plan-bundle-list-item__promo-info">
        {{ plan.promoInfoExtra }}
      </div>
    </div>
    <div class="plan-bundle-list-item__description">
      {{ plan.description }}
    </div>
    <div v-if="!isAuthenticated" class="plan-bundle-list-item__log-in">
      <span class="plan-bundle-list-item__log-in--bold">
        {{ `Already have ${plan.alreadyINE} subscription?` }}
      </span>
      <span
        class="plan-bundle-list-item__log-in-cta plan-bundle-list-item__log-in-cta--color-purple"
        @click="handleOpenModal"
      >
        Sign in to your account
      </span>
      to get the standalone certification.
    </div>
    <div class="plan-bundle-list-item__best-value">
      <strong>BUNDLE&nbsp;</strong>{{ plan.bundleShortInfo }}
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { updateCart } from '@/helpers/mixins'
import CONSTANTS from '@/constants'
import EJPTBadge from '@/assets/icons/certs/ejpt-badge.png'
import EEDABadge from '@/assets/icons/certs/eeda-badge.png'
import EWPTBadge from '@/assets/icons/certs/ewpt-badge.png'
import RadioButton from '@/components/shared/RadioButton.vue'

export default {
  name: 'PlanBundleItem',
  mixins: [updateCart],
  components: {
    RadioButton,
  },
  props: {
    plan: {
      type: Object,
      required: true,
      default: () => {
        return {}
      },
    },
    cardId: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    selected() {
      return this.plan.merchant_id === this.selectedPlan.merchant_id
    },
    price() {
      return this.plan.price_cents / 100
    },
    cardIdValue() {
      return this.cardId || this.plan.merchant_id
    },
  },
  created() {
    const {
      PLANS: { MONTHLY_EJPT, QUARTERLY_EJPT, EDA_PREMIUM, EWPT_PREMIUM },
    } = CONSTANTS
    switch (this.plan.merchant_id) {
      case MONTHLY_EJPT:
      case QUARTERLY_EJPT:
        this.plan.badge = EJPTBadge
        break
      case EDA_PREMIUM:
        this.plan.badge = EEDABadge
        break
      case EWPT_PREMIUM:
        this.plan.badge = EWPTBadge
        break
      default:
        break
    }
  },
  methods: {
    ...mapMutations({
      setIsSignInModalShown: 'app/IS_SIGN_IN_MODAL_SHOWN_UPDATED',
    }),
    handleOpenModal() {
      this.setIsSignInModalShown(true)
    },
  },
}
</script>

<style lang="scss" scoped>
.plan-bundle-list-item {
  padding: 40px 20px;
  border-radius: 10px 10px 0px 0px;
  background: var(--mid-gray-gradient);
  cursor: pointer;
  display: grid;
  grid-template-columns: repeat(2, 1fr) 50px;
  row-gap: 20px;
  position: relative;
  &:hover {
    background: var(--mid-gray-translucid-gradient);
  }
  &:active {
    transition: all 0.5s ease-out;
    background: var(--charcoal-gradient-two);
  }
  @include mobile {
    grid-template-columns: 110px auto 130px 30px;
    column-gap: 20px;
  }
  @include desktop {
    grid-template-columns: 130px auto 130px 30px;
    padding: 40px;
  }
  &__badge {
    &-wrapper {
      @include mobile {
        grid-area: 1/1/5/2;
      }
    }
    &-img {
      width: 110px;
      @include mobile {
        margin-top: 5px;
      }
      @include desktop {
        width: 130px;
      }
    }
  }
  &__price {
    text-align: right;
    font-size: rem(54px);
    font-weight: 700;
    line-height: rem(66px);
    grid-area: 1/2;
    @include mobile {
      grid-area: 1/3/2/4;
      font-size: rem(54px);
      line-height: rem(66px);
    }
    @include desktop {
      position: absolute;
    }
  }
  &__radio-button {
    grid-area: 1/3/2/4;
    margin-top: 15px;
    @include mobile {
      grid-area: 1/4/2/5;
    }
  }
  &__name {
    &-wrapper {
      grid-area: 2/1/3/4;
      @include mobile {
        grid-area: 1/2/2/3;
        display: flex;
        flex-flow: column;
        gap: 5px;
        width: 80%;
      }
    }
    font-size: rem(18px);
    font-weight: 500;
    line-height: rem(24px);
    margin: 0;
    font-family: var(--font-bold);
    &--promo-info {
      color: var(--purple-pale);
      font-size: rem(18px);
      font-weight: 500;
      line-height: rem(24px);
    }
  }
  &__promo-info {
    color: var(--dusty-gray);
    font-size: rem(12px);
    font-weight: 450;
    line-height: rem(14px);
  }
  &__description {
    grid-area: 3/1/4/4;
    font-size: rem(12px);
    font-weight: 450;
    line-height: rem(14px);
    @include mobile {
      grid-area: 2/2/3/5;
    }
    @include desktop {
      grid-area: 2/2/3/3;
      min-height: 80px;
    }
  }
  &__log-in {
    grid-area: 4/1/5/4;
    margin-bottom: 30px;
    @include mobile {
      grid-area: 3/2/4/5;
      margin-bottom: 10px;
    }
  }
  &__log-in {
    font-size: rem(14px);
    font-weight: 450;
    line-height: rem(20px);
    &--bold {
      font-weight: 700;
    }
    &-cta {
      text-decoration: underline;
      &--color-purple {
        color: var(--purple-pale);
      }
    }
  }
  &__best-value {
    grid-area: 5/1/6/4;
    background: var(--purple-gradient);
    position: absolute;
    top: 10px;
    right: -20px;
    width: calc(100% + 40px);
    border-radius: 0 0 10px 10px;
    height: 35px;
    display: flex;
    justify-content: center;
    align-items: center;

    color: var(--dusty-gray);
    font-size: rem(12px);
    font-weight: 450;
    line-height: rem(14px);
    @include mobile {
      grid-area: 4/1/5/5;
    }
    @include desktop {
      width: calc(100% + 80px);
      right: -40px;
    }
  }
}
</style>
